import type { Client, RequestContext } from "../client";
import type { GatewayLogLineResource } from "../resources/GatewayLogResource";
import type { ArgoCDApplicationResource, ArgoCDGatewayResource } from "../resources/index";

export type ListArgoCDApplicationsArgs = {
    skip: number;
    take: number;
    partialName: string;
    projectIds: string[];
    environmentIds: string[];
    tenantIds: string[];
    hideUnannotated: boolean;
};

export type ListArgoCDApplicationsResponse = { Resources: ArgoCDApplicationResource[]; TotalCount: number; FilteredCount: number | null };
export type GetArgoCDApplicationResponse = { Resource: ArgoCDApplicationResource };

export type UpdateArgoCDGatewayCommand = {
    SpaceId: string;
    Id: string;
    Name: string;
    EnvironmentIds: string[];
    WebUIUri: string | null;
};

export type GetArgoCDGatewayPodLogsArgs = {
    ShowPreviousContainer: boolean | null;
};

export class ArgoCDGatewayRepository {
    protected client: Client;

    baseUrl = "~/api/spaces/{spaceId}/argocdgateways";

    constructor(
        client: Client,
        private readonly requestContext: RequestContext
    ) {
        this.client = client;
    }

    async get(id: string): Promise<ArgoCDGatewayResource> {
        const r = await this.client.get<{ Resource: ArgoCDGatewayResource }>(this.requestContext, `${this.baseUrl}/{id}`, { id });
        return r.Resource;
    }

    async save(command: UpdateArgoCDGatewayCommand): Promise<ArgoCDGatewayResource> {
        const r = await this.client.post<{ Resource: ArgoCDGatewayResource }>(this.requestContext, `${this.baseUrl}/{id}`, command, { id: command.Id });
        return r.Resource;
    }

    async delete(id: string): Promise<void> {
        await this.client.delete(this.requestContext, `${this.baseUrl}/{id}`, undefined, { id });
    }

    async listApps(id: string, args?: Partial<ListArgoCDApplicationsArgs>): Promise<ListArgoCDApplicationsResponse> {
        const r = await this.client.get<ListArgoCDApplicationsResponse>(this.requestContext, `${this.baseUrl}/{id}/applications{?skip,take,partialName,projectIds*,environmentIds*,tenantIds*,hideUnannotated}`, {
            id,
            ...args,
        });
        return r;
    }

    async getApp(id: string, applicationId: string): Promise<GetArgoCDApplicationResponse> {
        return await this.client.get<GetArgoCDApplicationResponse>(this.requestContext, `${this.baseUrl}/{id}/applications/{applicationId}`, {
            id,
            applicationId,
        });
    }

    async getPodLogs(id: string, args?: Partial<GetArgoCDGatewayPodLogsArgs>): Promise<GatewayLogLineResource[]> {
        return await this.client.get<GatewayLogLineResource[]>(this.requestContext, `${this.baseUrl}/{id}/podlogs{?ShowPreviousContainer}`, { id, ...args });
    }
}
